import { Controller } from 'stimulus';
import videoHelpers from '../helpers/video';
import dialogEvents from '../helpers/dialog';
import getElementDocumentOffset from '../helpers/getElementDocumentOffset';

class Video extends Controller {
  connect() {
    this.player = null;
    this.startedSetup = false;

    if (this.hasDialogTarget) {
      dialogEvents(this.dialogTarget);

      this.dialogTarget.addEventListener('opened', () => {
        document.body.classList.add('is-locked');
      });

      this.dialogTarget.addEventListener('closed', () => {
        if (this.hasPosterTarget) {
          this.posterTarget.style.opacity = 1;
          this.posterTarget.style.visibility = 'visible';
        }

        document.body.classList.remove('is-locked');
        this.elementTarget.innerHTML = '';

        this.startedSetup = false;
      });
    }

    this.provider = this.elementTarget.getAttribute('data-provider');
    this.embedId = this.elementTarget.getAttribute('data-embed-id');
    this.embedUnlistedHash = this.elementTarget.getAttribute('data-embed-unlisted-hash');

    this.calculateDimensionsAndPositions();

    if (this.hasPosterTarget && !this.data.has('has-poster')) {
      this.setDefaultPoster();
    }

    if (this.data.get('lazy') === null) {
      this.setupPlayer();
    }
  }

  calculateDimensionsAndPositions() {
    this.documentOffsetY = getElementDocumentOffset(this.element).top;
    this.elementHeight = this.element.offsetHeight;
    this.windowHeight = window.innerHeight;
  }

  onWindowResize() {
    this.calculateDimensionsAndPositions();
  }

  onWindowScroll() {
    if (this.data.get('autoplay') !== null) {
      const inWindow = (
        this.documentOffsetY < (window.scrollY + this.windowHeight)
        && (this.documentOffsetY + this.elementHeight) > window.scrollY
      );

      // In view and player is initialized, play video...
      if (inWindow && this.player) {
        if (this.player && typeof this.player.playVideo === 'function') {
          this.player.playVideo();
        }

        if (this.player && typeof this.player.play === 'function') {
          this.player.play().catch(() => {
            //
          });
        }

        // In view and player is not initialized, setup player...
      } else if (inWindow) {
        this.onPlay();

        // Not in view, pause video...
      } else {
        if (this.player && typeof this.player.stopVideo === 'function') {
          this.player.pauseVideo();
        }

        if (this.player && typeof this.player.pause === 'function') {
          this.player.pause();
        }
      }
    }
  }

  onPlay() {
    if (this.hasDialogTarget) {
      this.dialogTarget.showModal();
    }

    this.setupPlayer();

    if (this.hasPosterTarget) {
      this.posterTarget.style.opacity = 0;
      this.posterTarget.style.visibility = 'hidden';
    }
  }

  setupPlayer() {
    if (this.startedSetup) {
      return;
    }

    this.startedSetup = true;

    if (this.provider === 'youtube') {
      Video.loadYoutubeApi()
        .then((YT) => {
          const youtubeWrapper = document.createElement('div');

          this.elementTarget.appendChild(youtubeWrapper);

          this.player = new YT.Player(youtubeWrapper, {
            height: '640',
            width: '640',
            videoId: this.embedId,
            playerVars: this.youtubeOptions(),
            events: {
              onReady: (event) => {
                event.target.playVideo();
              },
            },
          });
        });

      return;
    }

    if (this.provider === 'vimeo') {
      this.elementTarget.innerHTML = videoHelpers.createVimeoEmbed(this.embedId, this.embedUnlistedHash, this.data.get('autoplay') !== null);

      Video.loadVimeoApi()
        .then(() => {
          this.player = new window.Vimeo.Player(this.elementTarget, this.vimeoOptions());

          this.player.play()
            .catch(() => {
              // Mute and try again...
              this.player.setVolume(0);
              this.player.play().catch(() => {
                //
              });
            });
        });
    }
  }

  static loadYoutubeApi() {
    return new Promise((resolve) => {
      if (typeof window.YT !== 'undefined') {
        resolve(window.YT);
        return;
      }

      window.onYouTubeIframeAPIReady = () => {
        resolve(window.YT);
      };

      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    });
  }

  static loadVimeoApi() {
    return new Promise((resolve) => {
      if (typeof window.Vimeo !== 'undefined') {
        resolve(window.Vimeo);
        return;
      }

      const tag = document.createElement('script');

      tag.onload = () => {
        resolve(window.Vimeo);
      };

      tag.src = 'https://player.vimeo.com/api/player.js';

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    });
  }

  vimeoOptions() {
    let options = {
      dnt: true,
    };

    if (this.data.get('autoplay') !== null) {
      options = {
        ...options,
        muted: true,
        loop: true,
        title: false,
        byline: false,
      };
    }

    return options;
  }

  youtubeOptions() {
    let playerVars = {
      playsinline: 1,
      rel: 0,
    };

    if (this.data.get('autoplay') !== null) {
      playerVars = {
        ...playerVars,
        autoplay: 1,
        mute: 1,
        loop: 1,
        playlist: this.embedId,
      };
    }

    return playerVars;
  }

  setDefaultPoster() {
    let poster = null;

    if (this.provider === 'youtube') {
      poster = videoHelpers.getYoutubePoster(this.embedId);
    }

    if (this.provider === 'vimeo') {
      videoHelpers.getVimeoPoster(this.embedId, (src) => { poster = src; });
    }

    this.posterTarget.style.backgroundImage = `url(${poster})`;
  }
}

Video.targets = [
  'container',
  'element',
  'poster',
  'dialog',
];

export default Video;
